import { offerApiService } from '../services/offerApi';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { OfferStatus, type OfferRequest } from '../types/offer';
import { LoanOfferQueryKeys } from './useLoanOffer';

export const OfferQueryKeys = {
  offerRequested: 'offer-requested',
};

const ENDED_STATUSES: readonly string[] = [
  OfferStatus.Complete,
  OfferStatus.NoOfferAvailable,
];

// The guards route on the request status together with the offers list. A
// request that just ended is published only once the list is refreshed:
// read with the old list (preliminary offers only) a positive final offer
// looks like no offer, and the no-offer screen flashes or sticks.
const refreshOffersWhenEnded = async (
  queryClient: QueryClient,
  offer: OfferRequest
) => {
  const previous = queryClient.getQueryData<OfferRequest>([
    OfferQueryKeys.offerRequested,
  ]);
  if (!ENDED_STATUSES.includes(offer.status) || previous?.status === offer.status) {
    return;
  }
  // Nothing old to read: the list is fetched fresh by whoever needs it.
  if (!queryClient.getQueryData([LoanOfferQueryKeys.loanOfferState])) {
    return;
  }

  // Cancels a list request already in flight, which would answer with the
  // offers from before the request ended.
  await queryClient
    .invalidateQueries({
      queryKey: [LoanOfferQueryKeys.loanOfferState],
      refetchType: 'all',
    })
    .catch(() => undefined);
};

interface UseOfferRequestReturn {
  requestOffer: () => Promise<OfferRequest>;
  requestOfferError: Error | null;
  offer: OfferRequest | undefined;
  getOffer: () => Promise<OfferRequest | undefined>;
  isLoading: boolean;
}

export function useOfferRequest(
  fetchOffer: boolean = false
): UseOfferRequestReturn {
  const queryClient = useQueryClient();
  const inFlightRequest = useRef<Promise<OfferRequest> | null>(null);

  const offerQuery = useQuery({
    queryKey: [OfferQueryKeys.offerRequested],
    queryFn: async () => {
      const offer = await offerApiService.getOferRequested();
      if (offer) {
        await refreshOffersWhenEnded(queryClient, offer);
      }
      return offer;
    },
    enabled: fetchOffer,
  });

  const requestOfferMutation = useMutation({
    mutationFn: () => offerApiService.requestOffer(),
    onSuccess: async (offer) => {
      await refreshOffersWhenEnded(queryClient, offer);
      queryClient.setQueryData([OfferQueryKeys.offerRequested], offer);
    },
  });

  const getRequestedOffer = async () => {
    const result = await offerQuery.refetch();
    return result.data;
  };

  const requestOffer = () => {
    if (inFlightRequest.current) {
      return inFlightRequest.current;
    }
    const promise = requestOfferMutation
      .mutateAsync()
      .finally(() => {
        inFlightRequest.current = null;
      });
    inFlightRequest.current = promise;
    return promise;
  };

  return {
    requestOffer,
    requestOfferError: requestOfferMutation.error,
    offer: offerQuery.data,
    getOffer: getRequestedOffer,
    isLoading: offerQuery.isLoading || requestOfferMutation.isPending,
  };
}
